import cn from 'classnames'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { useAppDispatch, useAppSelector } from '@/core/store'
import { closeFullImageView } from '@/core/store/ui.store'

import { IconClose } from '../Ui/Icons'
import { IconLoupe } from '../Ui/Icons/IconLoupe'

const LOUPE_SIZE = 160
const LOUPE_ZOOM = 2.5
const TOUCH_LENS_OFFSET = 76

type UVRect = {
  u: number
  v: number
  rw: number
  rh: number
}

function getImageContentUV(clientX: number, clientY: number, img: HTMLImageElement): UVRect | null {
  const rect = img.getBoundingClientRect()
  const dispW = rect.width
  const dispH = rect.height
  const natW = img.naturalWidth
  const natH = img.naturalHeight
  if (!natW || !natH) return null
  const scale = Math.min(dispW / natW, dispH / natH)
  const rw = natW * scale
  const rh = natH * scale
  const offX = (dispW - rw) / 2
  const offY = (dispH - rh) / 2
  const x = clientX - rect.left
  const y = clientY - rect.top
  const ix = x - offX
  const iy = y - offY
  const u = ix / rw
  const v = iy / rh
  if (u < 0 || u > 1 || v < 0 || v > 1) return null
  return { u, v, rw, rh }
}

function clampLensCenter(cx: number, cy: number, size: number) {
  const m = 8
  const half = size / 2
  return {
    cx: Math.min(Math.max(half + m, cx), window.innerWidth - half - m),
    cy: Math.min(Math.max(half + m, cy), window.innerHeight - half - m),
  }
}

type LoupeImageProps = {
  src: string
  imgClassName?: string
  loupeActive: boolean
  interactionEnabled: boolean
}

function LoupeImage({ src, imgClassName, loupeActive, interactionEnabled }: LoupeImageProps) {
  const imgRef = useRef<HTMLImageElement>(null)
  const [lens, setLens] = useState<{ uv: UVRect; cx: number; cy: number } | null>(null)

  const handlePointer = useCallback(
    (e: React.PointerEvent) => {
      if (!loupeActive || !interactionEnabled) return
      const img = imgRef.current
      if (!img?.complete || !img.naturalWidth) return
      const uv = getImageContentUV(e.clientX, e.clientY, img)
      if (!uv) {
        setLens(null)
        return
      }
      const cx = e.clientX
      let cy = e.clientY
      if (e.pointerType === 'touch') {
        cy -= TOUCH_LENS_OFFSET
      }
      const c = clampLensCenter(cx, cy, LOUPE_SIZE)
      setLens({ uv, cx: c.cx, cy: c.cy })
    },
    [loupeActive, interactionEnabled],
  )

  const clearLens = useCallback(() => setLens(null), [])

  useEffect(() => {
    if (!loupeActive) setLens(null)
  }, [loupeActive])

  return (
    <div
      className={cn(
        'image-loupe-hit-wrap',
        loupeActive && interactionEnabled && 'image-loupe-hit-wrap--active',
        !interactionEnabled && 'image-loupe-hit-wrap--inactive',
      )}
      onPointerMove={handlePointer}
      onPointerEnter={handlePointer}
      onPointerLeave={clearLens}
      onPointerCancel={clearLens}
    >
      <img ref={imgRef} src={src} alt="" className={cn('image-loupe-main-img', imgClassName)} />
      {loupeActive &&
        lens &&
        interactionEnabled &&
        createPortal(
          <div
            className="image-loupe-lens"
            style={{
              position: 'fixed',
              left: lens.cx - LOUPE_SIZE / 2,
              top: lens.cy - LOUPE_SIZE / 2,
              width: LOUPE_SIZE,
              height: LOUPE_SIZE,
              zIndex: 203,
              pointerEvents: 'none',
            }}
          >
            <img
              src={src}
              alt=""
              className="image-loupe-lens-img"
              draggable={false}
              style={{
                position: 'absolute',
                width: lens.uv.rw * LOUPE_ZOOM,
                height: lens.uv.rh * LOUPE_ZOOM,
                left: LOUPE_SIZE / 2 - lens.uv.u * lens.uv.rw * LOUPE_ZOOM,
                top: LOUPE_SIZE / 2 - lens.uv.v * lens.uv.rh * LOUPE_ZOOM,
              }}
            />
          </div>,
          document.body,
        )}
    </div>
  )
}

export const FullScreenImage = () => {
  const { fullImageView } = useAppSelector((store) => store.uiState)
  const [activeIndex, setActiveIndex] = useState(0)
  const [loupeActive, setLoupeActive] = useState(false)
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (fullImageView) {
      setActiveIndex(0)
      setLoupeActive(false)
    } else {
      setLoupeActive(false)
    }
  }, [fullImageView])

  useEffect(() => {
    setLoupeActive(false)
  }, [activeIndex])

  const showLoupeChrome = Boolean(fullImageView?.gallery?.length)

  return (
    <>
      <div className={'full-screen-image-background' + (fullImageView ? ' active' : '')}></div>
      <div className={'full-screen-image' + (fullImageView ? ' active' : '')}>
        <div className="close-full-screen" onClick={() => dispatch(closeFullImageView())}>
          <IconClose white={true} />
        </div>

        {showLoupeChrome && (
          <button
            type="button"
            className={cn(
              'full-screen-loupe-toggle',
              loupeActive && 'full-screen-loupe-toggle--on',
            )}
            aria-pressed={loupeActive}
            aria-label={loupeActive ? 'Выключить лупу' : 'Включить лупу'}
            onClick={(e) => {
              setLoupeActive((v) => !v)
              e.currentTarget.blur()
            }}
          >
            <IconLoupe active={loupeActive} />
          </button>
        )}

        {/* Одиночное изображение */}
        {fullImageView?.gallery && fullImageView.gallery.length <= 1 && (
          <div className="full-screen-image-container">
            <div className="image-container">
              <LoupeImage
                src={fullImageView.gallery[0].uri}
                loupeActive={loupeActive}
                interactionEnabled
              />
              {fullImageView?.gallery[0]?.copyright && (
                <div className="image-copyright">{fullImageView.gallery[0].copyright}</div>
              )}
            </div>
            {fullImageView?.gallery[0]?.caption && (
              <div className="image-description">{fullImageView.gallery[0].caption}</div>
            )}
          </div>
        )}

        {/* Галерея */}
        {fullImageView?.gallery && fullImageView.gallery.length > 1 && (
          <div className="full-screen-image-container">
            <div className="swiper-container-gallery">
              <Swiper
                key={fullImageView.gallery.map((g) => g.uri).join('|')}
                spaceBetween={30}
                slidesPerView={1}
                modules={[Navigation]}
                navigation={{
                  prevEl: '.swiper-button-prev',
                  nextEl: '.swiper-button-next',
                }}
                allowTouchMove={!loupeActive}
                onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
                className="swiper-wrapper-custom"
              >
                {fullImageView?.gallery.map((img, idx) => (
                  <SwiperSlide key={idx}>
                    <div className="image-container">
                      <LoupeImage
                        src={img.uri}
                        imgClassName="object__photo__swiper"
                        loupeActive={loupeActive}
                        interactionEnabled={idx === activeIndex}
                      />
                      {img?.copyright && <div className="image-copyright">{img.copyright}</div>}
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
              <div className="swiper-button-prev"></div>
              <div className="swiper-button-next"></div>
            </div>

            {fullImageView?.gallery[activeIndex]?.caption && (
              <div className="image-description">{fullImageView.gallery[activeIndex].caption}</div>
            )}
          </div>
        )}
      </div>
    </>
  )
}
